// MultiCourtPicker — ActivityWizard Steg 3 (delspår multi-bana). Specifik-bana-läget:
// användaren väljer EXAKT N banor (N = deriveRequiredCourts(spelarantal), beslut #1).
// Listan är redan filtrerad till auto-poolen (max_players >= 4, beslut #4 — 2-spelar-banan
// frånvarande). När N banor valts spärras övriga ovalda (kan ej överskrida N); valda kan
// alltid avmarkeras. N=1 (4 spelare) = välj 1 bana (single-fallet bevarat).

import { LayoutGrid } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export interface CourtLite {
  id: string;
  name: string;
}

interface MultiCourtPickerProps {
  courts: CourtLite[];
  selectedCourtIds: string[];
  requiredCourts: number;
  onToggle: (courtId: string) => void;
}

export default function MultiCourtPicker({
  courts,
  selectedCourtIds,
  requiredCourts,
  onToggle,
}: MultiCourtPickerProps) {
  const { t } = useTranslation("admin");
  const selectedCount = selectedCourtIds.length;
  const atCapacity = selectedCount >= requiredCourts;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          {t("activityWizard.step3.multiCourtPicker.heading", {
            n: requiredCourts,
            defaultValue: `Välj ${requiredCourts} banor`,
          })}
        </span>
        <span data-testid="court-pick-count" className="text-xs tabular-nums text-muted-foreground">
          {t("activityWizard.step3.multiCourtPicker.count", {
            selected: selectedCount,
            n: requiredCourts,
            defaultValue: `${selectedCount} av ${requiredCourts} valda`,
          })}
        </span>
      </div>

      {courts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("activityWizard.step3.noCourts", { defaultValue: "Inga banor tillgängliga" })}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {courts.map((c) => {
            const selected = selectedCourtIds.includes(c.id);
            const disabled = !selected && atCapacity;
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={selected}
                disabled={disabled}
                data-testid={`court-pick-${c.id}`}
                onClick={() => onToggle(c.id)}
                className={cn(
                  "flex items-center gap-2.5 rounded-xl border p-2.5 text-left text-sm transition-all",
                  selected
                    ? "livfull-pill-selected font-medium shadow-sm"
                    : "border-border hover:border-primary/50",
                  disabled && "opacity-40 hover:border-border",
                )}
              >
                <span
                  className={cn(
                    "grid h-8 w-8 flex-none place-items-center rounded-lg transition-colors",
                    selected ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
                  )}
                >
                  <LayoutGrid className="h-4 w-4" />
                </span>
                <span className="truncate">{c.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
